import { Coordinate } from "maptalks"
import { BaseObject, ThreeLayer } from "maptalks.three"
import { Sprite, SpriteMaterial } from "three"
import _ from "lodash"

const DEFAULT_SCALE = 0.05
const DEFAULT_ALTITUDE = 0
const DEFAULT_ALPHATEST = 0.3
const DEFAULT_OPTIONS = {
  scale: DEFAULT_SCALE,
  altitude: DEFAULT_ALTITUDE,
  alphaTest: DEFAULT_ALPHATEST,
  highlight: {
    options: {
      scale: DEFAULT_SCALE * 1.25,
    },
    material: null,
  },
}

type ScaleValue = number | { x: number; y: number }
type AnchorValue = "center" | "bottom" | { x: number; y: number }

// Accepts uniform (number) or non-uniform ({x,y}) scale so non-square bubbles
// (e.g. teardrop aspect 50/43) can stretch vertically without distortion.
const normalizeScale = (s: ScaleValue): { x: number; y: number } =>
  typeof s === "number" ? { x: s, y: s } : s

const resolveAnchor = (a: AnchorValue): { x: number; y: number } => {
  if (a === "bottom") return { x: 0.5, y: 0 }
  if (a === "center") return { x: 0.5, y: 0.5 }
  return a
}

interface IOptions {
  scale: ScaleValue
  altitude: number
  alphaTest: number
  offsetAltitude?: number
  anchor?: AnchorValue
  renderOrder?: number
  visible?: boolean
  highlight?: {
    options: {
      altitude?: number
      scale?: ScaleValue
    }
    material?: SpriteMaterial | (() => SpriteMaterial)
  }
}

interface IState {
  options: { scale?: ScaleValue; altitude?: number }
  material: SpriteMaterial | (() => SpriteMaterial) | null
}

export class SpriteMarker extends BaseObject {
  #default: IState = { options: {}, material: null }
  #highlight: IState = { options: {}, material: null }
  #visible: boolean = true
  constructor(
    coordinate: Coordinate,
    options: IOptions,
    material: SpriteMaterial,
    layer: ThreeLayer,
    properties: object
  ) {
    super()
    //Initialize internal configuration
    this._initOptions(options)
    this._createGroup()
    const {
      altitude = DEFAULT_OPTIONS.altitude,
      scale = DEFAULT_OPTIONS.scale,
      highlight = DEFAULT_OPTIONS.highlight,
      alphaTest = DEFAULT_OPTIONS.alphaTest,
      offsetAltitude,
      anchor = "center",
      renderOrder,
      visible = true,
    } = options
    this.#visible = visible
    this.properties = { ...properties }

    // Legacy center-anchored sprites need a lift so they don't sink halfway
    // into the host surface; bottom-anchored sprites already sit on it.
    const resolvedOffset = offsetAltitude ?? (anchor === "center" ? 2 : 0)
    const modifiedAltitude = altitude + resolvedOffset

    this.#default = { options: { scale, altitude: modifiedAltitude }, material }
    this.#highlight = _.merge({}, DEFAULT_OPTIONS.highlight, highlight)

    /**
     * Add alphaTest to render only non-transparent pixels in PNG images.
     * [@Doc](https://threejs.org/docs/#api/en/materials/Material.alphaTest)
     */
    if (material && material instanceof SpriteMaterial)
      material.alphaTest = alphaTest

    const sprite = new Sprite(material)
    const ns = normalizeScale(scale)
    sprite.scale.set(ns.x, ns.y, Math.max(ns.x, ns.y))
    const anchorPoint = resolveAnchor(anchor)
    sprite.center.set(anchorPoint.x, anchorPoint.y)

    // Ensure the sprite is rendered after other objects
    if (renderOrder) sprite.renderOrder = renderOrder

    const obj3d = this.getObject3d()
    obj3d.add(sprite)
    if (!visible) sprite.visible = false
    // set object3d position
    const z = layer.altitudeToVector3(modifiedAltitude, modifiedAltitude).x
    const position = layer.coordinateToVector3(coordinate, z)
    _.set(this.properties, "default.position", position)
    this.getObject3d().position.copy(position)
  }

  // Different objects need to implement their own methods
  setSymbol(material: SpriteMaterial) {
    if (material && material instanceof SpriteMaterial) {
      const sprite = (this.getObject3d() as any).children[0]

      if (!sprite) return this

      sprite.material = material
      sprite.material.needsUpdate = true
    }
    return this
  }

  setScale(scaleX: number, scaleY: number = scaleX, scaleZ: number = scaleX) {
    const sprite = (this.getObject3d() as any).children[0]
    if (!sprite) return this

    sprite.scale.set(scaleX, scaleY, scaleZ)

    return this
  }

  // Different objects need to implement their own methods
  getSymbol(): SpriteMaterial {
    return (this.getObject3d() as any)?.children[0]?.material
  }

  highlight() {
    let { material, options } = this.#highlight
    // Lazy factory: defers texture creation until first highlight, then caches the result.
    if (typeof material === "function") {
      material = material()
      this.#highlight.material = material
    }
    if (material) this.setSymbol(material)
    if (options.scale) {
      const ns = normalizeScale(options.scale)
      this.setScale(ns.x, ns.y, Math.max(ns.x, ns.y))
    }
    if (options.altitude) this.setAltitude(options.altitude)
    if (!this.#visible) {
      const sprite = (this.getObject3d() as any).children[0]
      if (sprite) sprite.visible = true
    }
    return this
  }

  removeHighlight() {
    const { material, options } = this.#default
    if (material) this.setSymbol(material)
    if (options.scale) {
      const ns = normalizeScale(options.scale)
      this.setScale(ns.x, ns.y, Math.max(ns.x, ns.y))
    }
    if (options.altitude) this.setAltitude(options.altitude)
    if (!this.#visible) {
      const sprite = (this.getObject3d() as any).children[0]
      if (sprite) sprite.visible = false
    }
    return this
  }
}
